import { Component, signal, ChangeDetectionStrategy, inject, OnInit } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, tap } from 'rxjs';
import { take } from 'rxjs/operators';

import { noWhitespaceValidator } from '@core/common-legacy';
import { InfoboxComponent } from '@core/components-infobox-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
// eslint-disable-next-line boundaries/element-types
import { TellentMembersActions } from '@core/tellent-data'; // FIXME
import {
  ButtonComponent,
  ButtonLoadingDirective,
  DialogBodyComponent,
  DialogComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogService,
  DialogWidth,
  FormFieldComponent,
  GroupComponent,
  InputComponent,
  LabelComponent,
  RadioComponent,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';

import { ChangeLoginMethodConfirmDialogComponent } from './change-login-method-confirm-dialog.component';

export interface ChangeLoginMethodDialogData {
  username: string;
  memberGuid: string;
}

export interface ChangeLoginForm {
  email: FormControl<string>;
}

@Component({
  selector: 'rt-change-login-method-dialog',
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    LabelComponent,
    TranslatePipe,
    ButtonComponent,
    ButtonLoadingDirective,
    InfoboxComponent,
    GroupComponent,
    RadioComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './change-login-method-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangeLoginMethodDialogComponent
  extends DialogComponent<ChangeLoginMethodDialogData, boolean>
  implements OnInit
{
  private readonly formBuilder = inject(FormBuilder);
  private readonly dialogService = inject(DialogService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  protected username = signal('');
  protected memberGuid = signal('');
  protected loading = signal(false);
  protected checkboxValue = signal(true);

  protected form = this.formBuilder.nonNullable.group<ChangeLoginForm>({
    email: new FormControl<string>('', {
      validators: [Validators.required, noWhitespaceValidator, Validators.email],
      nonNullable: true,
    }),
  });

  private takeUntilDestroyed = injectUntilDestroyed();

  public ngOnInit(): void {
    const data = this.dialogData;
    if (data) {
      this.username.set(data.username);
      this.memberGuid.set(data.memberGuid);
    }
  }

  protected cancel(): void {
    this.dialogRef.close();
  }

  protected save(): void {
    this.loading.set(true);

    const data = {
      username: this.username(),
      memberGuid: this.memberGuid(),
      email: this.form.controls.email.value,
    };

    const { afterClosed } = this.dialogService.openDialog(
      ChangeLoginMethodConfirmDialogComponent,
      {
        username: data.username,
        memberGuid: data.memberGuid,
        email: data.email,
      },
      {
        width: DialogWidth.SmallX,
      },
    );

    afterClosed.subscribe(confirmed => {
      if (confirmed) {
        this.store.dispatch(
          TellentMembersActions.changeLoginMethod({
            payload: { email: data.email, guid: data.memberGuid },
          }),
        );

        combineLatest([
          this.actions$.pipe(ofType(TellentMembersActions.changeLoginMethodSuccess)),
          this.actions$.pipe(ofType(TellentMembersActions.changeLoginMethodFailed)),
        ])
          .pipe(
            take(1),
            tap(() => {
              this.loading.set(false);
            }),
            this.takeUntilDestroyed,
          )
          .subscribe();

        this.dialogRef.close();
      } else {
        this.loading.set(false);
      }
    });
  }

  protected onValueChange(value: boolean): void {
    if (value) {
      this.form.controls.email.reset('');
    }
    this.checkboxValue.set(value);
  }
}
